import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// --- Axios Instances ---

// Instance for public endpoints
const publicApi = axios.create({
  baseURL: API_URL,
});

// Instance for authenticated endpoints
const privateApi = axios.create({
  baseURL: API_URL,
});

privateApi.interceptors.request.use(
  (config) => {
    // Determine which token to use based on the URL or the current page
    const isAdminRoute = config.url?.startsWith('/admin/');
    const isCurrentlyOnAdminPage = window.location.pathname.startsWith('/admin');
    const userToken = localStorage.getItem('token');
    const adminToken = localStorage.getItem('admin_token');
    
    // Prioritize adminToken if we are hitting an admin endpoint OR if we are currently on an admin page
    const effectiveToken = (isAdminRoute || isCurrentlyOnAdminPage) 
      ? (adminToken || userToken) 
      : (userToken || adminToken);

    if (effectiveToken) {
      config.headers.Authorization = `Bearer ${effectiveToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for session expiration
privateApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      const isAdmin = !!localStorage.getItem('admin_token');
      localStorage.removeItem('token');
      localStorage.removeItem('admin_token');

      // Redirect to appropriate login page with expired flag
      const isFailedAdminRoute = error.config?.url?.startsWith('/admin/');
      const isCurrentlyOnAdminPage = window.location.pathname.startsWith('/admin');
      const shouldGoToAdminLogin = isFailedAdminRoute || isCurrentlyOnAdminPage;
      const loginPath = shouldGoToAdminLogin ? '/admin/login' : '/login';
      if (!window.location.pathname.includes('/login')) {
        window.location.href = `${loginPath}?expired=true`;
      }
    }
    return Promise.reject(error);
  }
);

// --- Error Handling ---

const handleApiError = (error: any, defaultMessage: string) => {
  if (error instanceof Error && !axios.isAxiosError(error)) {
    throw error; // Prevents double-handling
  }
  console.error("Full Error Object:", error);
  if (error.response) {
    console.error("API Error Response Data:", JSON.stringify(error.response.data, null, 2));
    const detail = error.response.data.detail;
    if (Array.isArray(detail)) {
      // Pydantic validation error list
      const msg = detail.map((d: any) => `${d.loc.join('.')}: ${d.msg}`).join(' | ');
      throw new Error(msg || defaultMessage);
    }
    throw new Error(detail || defaultMessage);
  } else if (error.request) {
    console.error("API Error Request (Wait timeout?):", error.request);
    throw new Error('No se pudo conectar con el servidor. Por favor, intÃ©ntelo de nuevo mÃ¡s tarde.');
  } else {
    console.error("API Error Setup (Maybe syntax/type error?):", error.message);
    throw new Error('OcurriÃ³ un error inesperado.');
  }
};

// --- Interfaces ---

export interface UserData {
  full_name: string;
  email: string;
  password?: string;
  phone?: string;
  nationality?: string;
  assistant_name?: string;
}

export interface UserUpdateData {
  full_name?: string;
  phone?: string;
  nationality?: string;
  assistant_name?: string;
}

export interface SubscriptionResponse {
  plan: string;
  status: string;
  is_cancelled: boolean;
  subscription_date: string | null;
  cycle_start_date: string | null;
  cycle_end_date: string | null;
  message_count: number;
  message_limit: number | null;
}

export interface UserResponse {
  id: number;
  full_name: string;
  email: string;
  assistant_name: string;
  phone?: string; // Added
  nationality?: string; // Added
  telegram_id: string | null;
  whatsapp_linked: boolean; // Added
  google_linked: boolean; // Added
  preferred_platform: string; // Added
  plan: string;
  is_admin: boolean;
  is_master?: boolean;
  is_active: boolean;
  message_count?: number;
  token_usage?: number;
  subscription?: SubscriptionResponse;

}

export interface PlanStatsResponse {
  plans: {
    plan: string;
    count: number;
    total_importe: number;
  }[];
  total_general: number;
}

export interface TokenUsageResponse {
  total_tokens: number;
  prompt_tokens: number;
  completion_tokens: number;
}

export interface SessionLogItem {
  id: number;
  user_id: number;
  full_name?: string;
  action: string;
  timestamp: string;
}

export interface TelegramLinkResponse {
  link: string;
  link_web?: string;
}

export interface ChatResponse {
  response: string;
}

export interface SupportTicket {
  id: number;
  user_id: number;
  user?: {
    full_name: string;
    email: string;
  };
  issue: string;
  status: 'open' | 'closed' | 'in_progress';
  priority: 'normal' | 'urgent_devops';
  requires_human: boolean;
  is_read: boolean;
  chat_history?: string; // JSON
  created_at: string;
  resolved_at: string | null;
}

// --- API Functions ---
export const registerAdmin = async (adminData: { full_name: string; email: string; password: string }) => {
  try {
    // 1. Create standard user
    await registerUser({
      email: adminData.email,
      password: adminData.password,
      full_name: adminData.full_name,
      assistant_name: "CUDA"
    });

    // 2. Login to get token
    const formData = new FormData();
    formData.append('username', adminData.email);
    formData.append('password', adminData.password);
    await loginUser(formData); // Stores token in localStorage

    // 3. Request Admin Access
    const response = await requestAdminAccess(); // Uses token
    return response;

  } catch (error) {
    handleApiError(error, 'Error al registrar administrador.');
  }
};

export const registerUser = async (userData: UserData) => {
  try {
    const response = await publicApi.post('/users/', userData);
    return response.data;
  } catch (error) {
    handleApiError(error, 'OcurriÃ³ un error durante el registro.');
  }
};

export const loginUser = async (loginData: FormData | URLSearchParams) => {
  try {
    console.log("Attempting user login...");
    const response = await publicApi.post('/token', loginData, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
    console.log("Login API Response:", response.data);
    if (response.data.access_token) {
      localStorage.setItem('token', response.data.access_token);
      console.log("User Token stored successfully");
    }
    return response.data;
  } catch (error) {
    console.error("Login API Error:", error);
    handleApiError(error, 'OcurriÃ³ un error durante el inicio de sesiÃ³n.');
  }
};

export const adminLogin = async (loginData: FormData | URLSearchParams) => {
  try {
    console.log("Attempting admin login...");
    const response = await publicApi.post('/admin/token', loginData, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
    console.log("Admin Login API Response:", response.data);
    if (response.data.access_token) {
      localStorage.setItem('admin_token', response.data.access_token);
      console.log("Admin Token stored successfully");
    }
    return response.data;
  } catch (error) {
    console.error("Admin Login API Error:", error);
    handleApiError(error, 'OcurriÃ³ un error durante el inicio de sesiÃ³n de administrador.');
  }
};

export const logout = async () => {
  try {
    const isAdmin = !!localStorage.getItem('admin_token');
    if (isAdmin) {
      await privateApi.post('/logout');
    }
  } catch (e) {
    console.error("Logout error (ignoring):", e);
  } finally {
    localStorage.removeItem('token');
    localStorage.removeItem('admin_token');
  }
};

export const getCurrentUser = async (): Promise<UserResponse | undefined> => {
  try {
    const response = await privateApi.get<UserResponse>('/users/me');
    return response.data;
  } catch (error) {
    handleApiError(error, 'No se pudo obtener informaciÃ³n del usuario.');
  }
};

export const updateUserProfile = async (updateData: UserUpdateData): Promise<UserResponse | undefined> => {
  try {
    const response = await privateApi.put<UserResponse>('/users/me', updateData);
    return response.data;
  } catch (error) {
    handleApiError(error, 'No se pudo actualizar el perfil.');
  }
};

export const unlinkTelegram = async (): Promise<UserResponse | undefined> => {
  try {
    const response = await privateApi.post<UserResponse>('/users/me/unlink-telegram');
    return response.data;
  } catch (error) {
    handleApiError(error, 'No se pudo desvincular la cuenta de Telegram.');
  }
};

export const getTelegramLink = async (): Promise<TelegramLinkResponse | undefined> => {
  try {
    const response = await privateApi.get<TelegramLinkResponse>('/users/me/telegram-link');
    return response.data;
  } catch (error) {
    handleApiError(error, 'No se pudo generar el enlace de Telegram.');
  }
};

export const getWhatsAppLink = async (): Promise<TelegramLinkResponse | undefined> => {
  try {
    const response = await privateApi.get<TelegramLinkResponse>('/users/me/whatsapp-link');
    return response.data;
  } catch (error) {
    handleApiError(error, 'No se pudo generar el enlace de WhatsApp.');
  }
};

export const unlinkWhatsApp = async (): Promise<UserResponse | undefined> => {
  try {
    const response = await privateApi.post<UserResponse>('/users/me/unlink-whatsapp');
    return response.data;
  } catch (error) {
    handleApiError(error, 'No se pudo desvincular WhatsApp.');
  }
};

// --- Payment API ---

export const createPaymentOrder = async (plan: string): Promise<any> => {
  try {
    const response = await privateApi.post('/payments/create-order', { plan });
    return response.data;
  } catch (error) {
    handleApiError(error, 'No se pudo crear la orden de pago.');
  }
};

export const capturePaymentOrder = async (orderID: string): Promise<any> => {
  try {
    const response = await privateApi.post('/payments/capture-order', { orderID });
    return response.data;
  } catch (error) {
    handleApiError(error, 'No se pudo capturar el pago.');
  }
};

export const activateSubscription = async (subscriptionID: string, plan: string): Promise<any> => {
  try {
    const response = await privateApi.post('/payments/activate-subscription', { subscriptionID, plan });
    return response.data;
  } catch (error) {
    handleApiError(error, 'No se pudo activar la suscripciÃ³n.');
  }
};

export const getPaymentLogs = async (): Promise<any[]> => {
  try {
    const response = await privateApi.get('/admin/payment-logs');
    return response.data;
  } catch (error) {
    handleApiError(error, 'No se pudieron obtener los logs de pago.');
    return [];
  }
};

export interface MessageHistoryItem {
  role: string;
  content: string;
}

export interface ReminderItem {
  id: number;
  message: string;
  reminder_date: string; // Assuming ISO string format
  sent: boolean;
}

export const sendMessage = async (message: string): Promise<ChatResponse | undefined> => {
  try {
    const response = await privateApi.post<ChatResponse>('/users/me/chat', { message });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Error al enviar mensaje.');
  }
};

export const getChatHistory = async (): Promise<MessageHistoryItem[] | undefined> => {
  try {
    const response = await privateApi.get<MessageHistoryItem[]>('/users/me/chat/history');
    return response.data;
  } catch (error) {
    handleApiError(error, 'Error al cargar historial el del chat.');
  }
};

export const getUserReminders = async (): Promise<ReminderItem[] | undefined> => {
  try {
    const response = await privateApi.get<ReminderItem[]>('/users/me/reminders');
    return response.data;
  } catch (error) {
    handleApiError(error, 'Error al cargar recordatorios.');
  }
};

export const createReminder = async (message: string, date: string): Promise<ReminderItem | undefined> => {
  try {
    const response = await privateApi.post<ReminderItem>('/users/me/reminders', { message, reminder_date: date });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Error al crear recordatorio.');
  }
};

export const getAllUsers = async (): Promise<UserResponse[] | undefined> => {
  try {
    const response = await privateApi.get<UserResponse[]>('/admin/users', {
      headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }, // Use admin token
    });
    return response.data;
  } catch (error) {
    handleApiError(error, 'No se pudieron obtener los usuarios.');
  }
};

export const toggleUserActiveStatus = async (userId: number): Promise<UserResponse | undefined> => {
  try {
    const response = await privateApi.post<UserResponse>(`/admin/users/${userId}/toggle_active`, {}, {
      headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }, // Use admin token
    });
    return response.data;
  } catch (error) {
    handleApiError(error, 'No se pudo cambiar el estado del usuario.');
  }
};

export const setUserPlan = async (userId: number, plan: string): Promise<UserResponse | undefined> => {
  try {
    const uppercasePlan = plan.toUpperCase();
    const response = await privateApi.post<UserResponse>(`/admin/users/${userId}/set_plan`, { plan: uppercasePlan }, {
      headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }, // Use admin token
    });
    return response.data;
  } catch (error) {
    handleApiError(error, 'No se pudo establecer el plan del usuario.');
  }
};

export const getTokenUsage = async (): Promise<TokenUsageResponse | undefined> => {
  try {
    const response = await privateApi.get<TokenUsageResponse>('/admin/token-usage', {
      headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }, // Use admin token
    });
    return response.data;
  } catch (error) {
    handleApiError(error, 'No se pudo obtener el uso de tokens.');
  }
};

// --- Master Admin API ---

export const requestAdminAccess = async (): Promise<UserResponse | undefined> => {
  try {
    const response = await privateApi.post<UserResponse>('/users/request-admin');
    return response.data;
  } catch (error) {
    handleApiError(error, 'Error al solicitar acceso de administrador');
  }
};

export const getAdminRequests = async (): Promise<UserResponse[] | undefined> => {
  try {
    const response = await privateApi.get<UserResponse[]>('/admin/requests');
    return response.data;
  } catch (error) {
    handleApiError(error, 'Error al obtener solicitudes de administrador');
  }
};

export const approveAdmin = async (userId: number): Promise<UserResponse | undefined> => {
  try {
    const response = await privateApi.post<UserResponse>(`/admin/approve/${userId}`);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Error al aprobar administrador');
  }
};

export const rejectAdmin = async (userId: number): Promise<UserResponse | undefined> => {
  try {
    const response = await privateApi.post<UserResponse>(`/admin/reject/${userId}`);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Error al rechazar administrador');
  }
};

export const deleteUser = async (userId: number) => {
  try {
    await privateApi.delete(`/admin/users/${userId}`);
    return true;
  } catch (error) {
    handleApiError(error, 'Error al eliminar usuario.');
  }
};

export const getAdminSessions = async (): Promise<SessionLogItem[] | undefined> => {
  try {
    const response = await privateApi.get<SessionLogItem[]>('/admin/sessions');
    return response.data;
  } catch (error) {
    handleApiError(error, 'Error al obtener historial de sesiones.');
  }
};

export const clearChatHistory = async () => {
  try {
    await privateApi.delete('/users/me/chat/history');
    return true;
  } catch (error) {
    handleApiError(error, 'Error al limpiar el historial del chat.');
  }
};

export const getMySubscription = async (): Promise<SubscriptionResponse | undefined> => {
  try {
    const response = await privateApi.get<SubscriptionResponse>('/users/me/subscription');
    return response.data;
  } catch (error) {
    handleApiError(error, 'Error al obtener suscripciÃ³n.');
  }
};

export const cancelSubscription = async () => {
  try {
    const response = await privateApi.post('/users/me/subscription/cancel');
    return response.data;
  } catch (error) {
    handleApiError(error, 'Error al cancelar suscripciÃ³n.');
  }
};

export const getAdminPlanStats = async (): Promise<PlanStatsResponse | undefined> => {
  try {
    const response = await privateApi.get<PlanStatsResponse>('/admin/stats/plans');
    return response.data;
  } catch (error) {
    handleApiError(error, 'Error al obtener estadÃ­sticas de planes.');
  }
};

export const sendAdminNotification = async (
  message: string,
  targetType: 'ALL' | 'INDIVIDUAL' | 'MULTIPLE' | 'PLAN' = 'ALL',
  userId?: number,
  userIds?: number[],
  plan?: string
) => {
  try {
    const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
    const response = await privateApi.post('/admin/notifications/send', {
      message,
      target_type: targetType,
      user_id: userId,
      user_ids: userIds,
      plan: plan
    }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Error al enviar la notificación.');
  }
};

export const getMyNotifications = async () => {
  try {
    const response = await privateApi.get('/users/me/notifications');
    return response.data;
  } catch (error) {
    handleApiError(error, 'Error al obtener tus notificaciones.');
  }
};

export const getDevOpsReport = async (): Promise<{ report: string } | undefined> => {
  try {
    const response = await privateApi.get<{ report: string }>('/admin/devops/report', {
      headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
    });
    return response.data;
  } catch (error) {
    handleApiError(error, 'No se pudo obtener el reporte DevOps.');
  }
};

export const runDevOpsRepair = async (issue_type: string): Promise<{ status: string; message: string } | undefined> => {
  try {
    const response = await privateApi.post<{ status: string; message: string }>('/admin/devops/repair', { issue_type }, {
      headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
    });
    return response.data;
  } catch (error) {
    handleApiError(error, 'No se pudo ejecutar la reparaciÃ³n DevOps.');
  }
};

export const sendDevOpsMessage = async (message: string): Promise<ChatResponse | undefined> => {
  try {
    const response = await privateApi.post<ChatResponse>('/admin/devops/chat', { message }, {
      headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
    });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Error al enviar mensaje a DevOps.');
  }
};

export const getDevOpsHistory = async (): Promise<MessageHistoryItem[] | undefined> => {
  try {
    const response = await privateApi.get<MessageHistoryItem[]>('/admin/devops/chat/history', {
      headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
    });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Error al cargar historial DevOps.');
  }
};

export const clearDevOpsHistory = async () => {
  try {
    const response = await privateApi.delete('/admin/devops/chat/clear', {
      headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
    });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Error al limpiar historial DevOps.');
  }
};

// --- Admin Support Ticket API ---

export const getAdminTickets = async (): Promise<SupportTicket[] | undefined> => {
  try {
    const response = await privateApi.get<SupportTicket[]>('/admin/support/tickets', {
      headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
    });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Error al obtener tickets de soporte.');
  }
};

export const getTicketDetails = async (ticketId: number): Promise<{ ticket: SupportTicket; history: MessageHistoryItem[] } | undefined> => {
  try {
    const response = await privateApi.get<{ ticket: SupportTicket; history: MessageHistoryItem[] }>(`/admin/support/tickets/${ticketId}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
    });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Error al obtener detalles del ticket.');
  }
};

export const replyToTicket = async (ticketId: number, message: string) => {
  try {
    const response = await privateApi.post(`/admin/support/tickets/${ticketId}/reply`, { message }, {
      headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
    });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Error al enviar respuesta al ticket.');
  }
};

export const closeTicket = async (ticketId: number) => {
  try {
    const response = await privateApi.post(`/admin/support/tickets/${ticketId}/close`, {}, {
      headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
    });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Error al cerrar el ticket.');
  }
};
// --- User Support Chat ---

export const sendSupportMessage = async (message: string): Promise<ChatResponse | undefined> => {
  try {
    const response = await privateApi.post<ChatResponse>('/users/me/support/chat', { message });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Error al enviar mensaje de soporte.');
  }
};

export const closeSupportSession = async () => {
  try {
    const response = await privateApi.post('/users/me/support/close');
    return response.data;
  } catch (error) {
    // Silent fail for cleanup tasks
    console.warn('Silent fail: closeSupportSession', error);
  }
};

export const getSupportHistory = async (): Promise<MessageHistoryItem[] | undefined> => {
  try {
    const response = await privateApi.get<MessageHistoryItem[]>('/users/me/support/history');
    return response.data;
  } catch (error) {
    handleApiError(error, 'Error al cargar historial de soporte.');
  }
};

export const getGoogleAuthUrl = async () => {
    try {
        const response = await privateApi.get('/google/auth-url');
        return response.data;
    } catch (error) {
        handleApiError(error, 'Error al obtener URL de sincronizaciÃ³n de Google.');
    }
};

export const unlinkGoogle = async () => {
    try {
        const response = await privateApi.post('/google/unlink');
        return response.data;
    } catch (error) {
        handleApiError(error, 'Error al desvincular Google.');
    }
};

// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// MONITORING API â€” Estado en tiempo real del sistema
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export interface WhatsAppStatus {
  state: 'open' | 'connecting' | 'error' | 'unknown';
  ok: boolean;
}

export interface SystemMetrics {
  active_users_30d: number;
  total_users: number;
  messages_today: number;
  reminders_today: number;
  google_linked: number;
}

export interface SystemLogs {
  logs: string[];
}

export const getWhatsAppStatus = async (): Promise<WhatsAppStatus | undefined> => {
  try {
    const res = await privateApi.get<WhatsAppStatus>('/admin/status/whatsapp');
    return res.data;
  } catch (error) {
    console.warn('WhatsApp status check failed', error);
    return { state: 'error', ok: false };
  }
};

export const getSystemMetrics = async (): Promise<SystemMetrics | undefined> => {
  try {
    const res = await privateApi.get<SystemMetrics>('/admin/status/metrics');
    return res.data;
  } catch (error) {
    console.warn('System metrics fetch failed', error);
    return undefined;
  }
};

export const getSystemLogs = async (): Promise<SystemLogs | undefined> => {
  try {
    const res = await privateApi.get<SystemLogs>('/admin/status/logs');
    return res.data;
  } catch (error) {
    console.warn('System logs fetch failed', error);
    return undefined;
  }
};

export const reconnectWhatsApp = async (): Promise<{ ok: boolean; message: string } | undefined> => {
  try {
    const res = await privateApi.post<{ ok: boolean; message: string }>('/admin/actions/reconnect-whatsapp');
    return res.data;
  } catch (error) {
    handleApiError(error, 'Error al reconectar WhatsApp.');
  }
};

export const createMPSubscription = async (plan: string): Promise<{init_point: string}> => {
  const response = await privateApi.post("/payments/mp/create-subscription", { plan });
  return response.data;
};

export const resetUserMessages = async (userId: number): Promise<{status: string; message: string}> => {
  const response = await privateApi.post(`/admin/users/${userId}/reset-messages`);
  return response.data;
};
